import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { toast } from "sonner";
import { 
  Plus, 
  Image, 
  Video, 
  FileText, 
  Heart, 
  MessageCircle, 
  Share2, 
  Edit3, 
  Trash2, 
  Send,
  Camera,
  Mic,
  MapPin,
  Clock,
  Eye,
  Users
} from "lucide-react";

const AgencyFeedManager = ({ agenziaId, agenziaNome }) => {
  const [agencyPosts, setAgencyPosts] = useState([]);
  const [showCreatePost, setShowCreatePost] = useState(false);
  const [newPost, setNewPost] = useState({
    tipo: 'testo',
    contenuto: '',
    immagine: null,
    video: null
  });

  // Mock posts dell'agenzia (in futuro da API)
  const mockAgencyPosts = [
    {
      id: 1,
      tipo: 'job_posting',
      contenuto: '🏢 Stiamo cercando un agente commerciale senior per la nostra sede di Milano! Esperienza minima 3 anni nel settore immobiliare. Ottimo pacchetto retributivo e commissioni competitive.',
      immagine: null,
      video: null,
      data_creazione: '2024-10-06T10:30:00Z',
      likes: 15,
      commenti: 4,
      condivisioni: 2,
      visualizzazioni: 89
    },
    {
      id: 2,
      tipo: 'success_story',
      contenuto: '🎉 Grande successo! Il nostro team ha chiuso 8 vendite questa settimana. Complimenti a tutti gli agenti per l\'impegno e la professionalità!',
      immagine: 'https://images.unsplash.com/photo-1560520653-9e0e4c89eb11?w=400&h=300&fit=crop',
      video: null,
      data_creazione: '2024-10-05T14:20:00Z',
      likes: 23,
      commenti: 7,
      condivisioni: 5,
      visualizzazioni: 156
    },
    {
      id: 3,
      tipo: 'company_news',
      contenuto: '📈 Espansione in corso! Siamo felici di annunciare l\'apertura della nostra nuova sede a Roma. Nuove opportunità di carriera in arrivo!',
      immagine: null,
      video: null,
      data_creazione: '2024-10-04T09:15:00Z',
      likes: 31,
      commenti: 12,
      condivisioni: 8,
      visualizzazioni: 234
    }
  ];

  useEffect(() => {
    // Carica i post dell'agenzia (mock per ora)
    setAgencyPosts(mockAgencyPosts);
  }, []);

  const handleCreatePost = () => {
    if (!newPost.contenuto.trim()) {
      toast.error("Inserisci il contenuto del post");
      return;
    }

    const post = {
      id: Date.now(),
      tipo: newPost.tipo,
      contenuto: newPost.contenuto,
      immagine: newPost.immagine,
      video: newPost.video,
      data_creazione: new Date().toISOString(),
      likes: 0,
      commenti: 0,
      condivisioni: 0,
      visualizzazioni: 0
    };

    setAgencyPosts([post, ...agencyPosts]);
    setNewPost({ tipo: 'testo', contenuto: '', immagine: null, video: null });
    setShowCreatePost(false);
    toast.success("Post pubblicato con successo!");
  };

  const formatTimeAgo = (dateString) => {
    const now = new Date();
    const postDate = new Date(dateString);
    const diffInHours = Math.floor((now - postDate) / (1000 * 60 * 60));
    
    if (diffInHours < 1) return "Ora";
    if (diffInHours < 24) return `${diffInHours}h fa`;
    const diffInDays = Math.floor(diffInHours / 24);
    return `${diffInDays}g fa`;
  };

  const getPostTypeLabel = (tipo) => {
    const types = {
      'job_posting': { label: 'Offerta Lavoro', color: 'bg-blue-100 text-blue-700' },
      'success_story': { label: 'Successo', color: 'bg-green-100 text-green-700' },
      'company_news': { label: 'Notizia', color: 'bg-purple-100 text-purple-700' },
      'testo': { label: 'Post', color: 'bg-gray-100 text-gray-700' }
    };
    return types[tipo] || types.testo;
  };

  return (
    <div className="space-y-6">
      {/* Header con Crea Post */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2">
              <span className="text-lg">📝</span>
              I tuoi Post
            </CardTitle>
            <Button 
              onClick={() => setShowCreatePost(!showCreatePost)}
              className="bg-gradient-to-r from-blue-500 to-purple-600 text-white"
            >
              <Plus className="w-4 h-4 mr-2" />
              Nuovo Post
            </Button>
          </div>
        </CardHeader>
        
        {/* Form Crea Post */}
        {showCreatePost && (
          <CardContent className="border-t border-gray-100 pt-4">
            <div className="space-y-4">
              <div className="flex gap-2 mb-4">
                <Button
                  variant={newPost.tipo === 'testo' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setNewPost({...newPost, tipo: 'testo'})}
                >
                  <FileText className="w-4 h-4 mr-1" />
                  Testo
                </Button>
                <Button
                  variant={newPost.tipo === 'job_posting' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setNewPost({...newPost, tipo: 'job_posting'})}
                >
                  <Users className="w-4 h-4 mr-1" />
                  Offerta
                </Button>
                <Button
                  variant={newPost.tipo === 'success_story' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setNewPost({...newPost, tipo: 'success_story'})}
                >
                  🎉 Successo
                </Button>
              </div>

              <textarea
                value={newPost.contenuto}
                onChange={(e) => setNewPost({...newPost, contenuto: e.target.value})}
                placeholder="Cosa vuoi condividere con i candidati?"
                className="w-full p-3 border border-gray-200 rounded-lg resize-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                rows={4}
              />

              <div className="flex gap-2">
                <Button variant="outline" size="sm">
                  <Image className="w-4 h-4 mr-1" />
                  Immagine
                </Button>
                <Button variant="outline" size="sm">
                  <Video className="w-4 h-4 mr-1" />
                  Video
                </Button>
              </div>

              <div className="flex gap-2 justify-end">
                <Button 
                  variant="outline" 
                  onClick={() => setShowCreatePost(false)}
                >
                  Annulla
                </Button>
                <Button 
                  onClick={handleCreatePost}
                  className="bg-gradient-to-r from-blue-500 to-purple-600 text-white"
                >
                  <Send className="w-4 h-4 mr-2" />
                  Pubblica
                </Button>
              </div>
            </div>
          </CardContent>
        )}
      </Card>

      {/* Lista Post dell'Agenzia */}
      <div className="space-y-4">
        {agencyPosts.map((post) => {
          const typeInfo = getPostTypeLabel(post.tipo);
          return (
            <Card key={post.id} className="hover:shadow-md transition-shadow">
              <CardHeader className="pb-3">
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-gradient-to-r from-blue-500 to-purple-600 rounded-full flex items-center justify-center text-white font-bold">
                      {agenziaNome?.charAt(0) || 'A'}
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <h4 className="font-semibold text-gray-900">{agenziaNome || 'Agenzia'}</h4>
                        <Badge className={typeInfo.color}>
                          {typeInfo.label}
                        </Badge>
                      </div>
                      <p className="text-sm text-gray-500 flex items-center gap-1">
                        <Clock className="w-3 h-3" />
                        {formatTimeAgo(post.data_creazione)}
                      </p>
                    </div>
                  </div>
                  <div className="flex gap-1">
                    <Button variant="ghost" size="sm">
                      <Edit3 className="w-4 h-4" />
                    </Button>
                    <Button variant="ghost" size="sm">
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </CardHeader>
              
              <CardContent>
                <p className="text-gray-700 mb-4 whitespace-pre-line">{post.contenuto}</p>
                
                {post.immagine && (
                  <div className="mb-4">
                    <img 
                      src={post.immagine} 
                      alt="Post content" 
                      className="w-full rounded-lg max-h-80 object-cover"
                    />
                  </div>
                )}

                {/* Statistiche Post */}
                <div className="flex items-center justify-between text-sm text-gray-500 mb-3">
                  <div className="flex items-center gap-4">
                    <span className="flex items-center gap-1">
                      <Heart className="w-4 h-4" />
                      {post.likes}
                    </span>
                    <span className="flex items-center gap-1">
                      <MessageCircle className="w-4 h-4" />
                      {post.commenti}
                    </span>
                    <span className="flex items-center gap-1">
                      <Share2 className="w-4 h-4" />
                      {post.condivisioni}
                    </span>
                    <span className="flex items-center gap-1">
                      <Eye className="w-4 h-4" />
                      {post.visualizzazioni}
                    </span>
                  </div>
                </div>

                <div className="border-t border-gray-100 pt-3">
                  <div className="flex gap-2">
                    <Button variant="ghost" size="sm" className="flex-1">
                      <Heart className="w-4 h-4 mr-1" />
                      Mi piace
                    </Button>
                    <Button variant="ghost" size="sm" className="flex-1">
                      <MessageCircle className="w-4 h-4 mr-1" />
                      Commenta
                    </Button>
                    <Button variant="ghost" size="sm" className="flex-1">
                      <Share2 className="w-4 h-4 mr-1" />
                      Condividi
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {agencyPosts.length === 0 && (
        <Card>
          <CardContent className="text-center py-12">
            <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <FileText className="w-8 h-8 text-gray-400" />
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">Nessun post pubblicato</h3>
            <p className="text-gray-500 mb-4">Inizia a condividere contenuti per attrarre nuovi candidati</p>
            <Button 
              onClick={() => setShowCreatePost(true)}
              className="bg-gradient-to-r from-blue-500 to-purple-600 text-white"
            >
              <Plus className="w-4 h-4 mr-2" />
              Crea il tuo primo post
            </Button>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default AgencyFeedManager;